import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Copy, Share2, Printer, ExternalLink, AlertTriangle, XCircle, Clock,
} from "lucide-react";
import { EnvironmentBanner, EnvironmentChip } from "@/components/EnvironmentBanner";

/**
 * Public THOM Verified™ Certificate + Badge validation page.
 *
 * Asset-agnostic. Renders sanitised public view + LIVE badge that always
 * reflects the registry's current status (revoked / expired / suspended
 * certificates never display a green Verified badge).
 */
export default function VerifiedCertificatePage() {
  const { certNumber } = useParams();
  const [look, setLook] = useState(certNumber || "");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const load = async (num) => {
    if (!num) return;
    setBusy(true); setNotFound(false);
    try {
      const r = await api.get(`/public/certificates/${num}`);
      setD(r.data);
    } catch (e) {
      setD(null); setNotFound(true);
    } finally { setBusy(false); }
  };

  useEffect(() => { if (certNumber) load(certNumber); }, [certNumber]);

  const badgeUrl = (variant, fmt = "svg") => `${backendUrl}/api/public/certificates/${d?.certificate_number}/badge?variant=${variant}&format=${fmt}`;
  const pdfUrl = () => `${backendUrl}/api/public/certificates/${d?.certificate_number}/pdf`;

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const share = () => {
    const url = `${window.location.origin}/verify/${d.certificate_number}`;
    if (navigator.share) navigator.share({ title: `THOM Verified™ ${d.certificate_number}`, url });
    else { copy(url); toast.success("Verification link copied"); }
  };

  const outcomeTone = (o) => {
    if (o === "verified") return "bg-emerald-600 text-white";
    if (o === "verified_with_disclosures" || o === "conditionally_verified") return "bg-emerald-700 text-white";
    if (o === "verification_pending") return "bg-amber-500 text-white";
    return "bg-red-600 text-white";
  };
  const outcomeIcon = (o) => {
    if (o === "verified") return <ShieldCheck className="h-4 w-4" />;
    if (o === "verified_with_disclosures" || o === "conditionally_verified") return <ShieldCheck className="h-4 w-4" />;
    if (o === "verification_pending") return <Clock className="h-4 w-4" />;
    if (o === "certificate_revoked") return <XCircle className="h-4 w-4" />;
    return <AlertTriangle className="h-4 w-4" />;
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="kicker">THOM Verify™ · Public Verification Registry</div>
          <h1 className="display text-3xl mt-1">Verified Certificate Lookup</h1>
          <p className="text-sm text-muted-foreground mt-1">Enter or scan a THOM Verified™ certificate number. Status is fetched live from the registry — revoked or expired certificates never display a green Verified badge.</p>
        </div>

        <div className="flex gap-2 mb-8 max-w-xl">
          <Input placeholder="TVC-2026-XXXX-XXXX" value={look} onChange={(e) => setLook(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && load(look)} data-testid="cert-input" />
          <Button onClick={() => load(look)} disabled={busy} data-testid="cert-lookup">Verify</Button>
        </div>

        {notFound && <div className="border border-red-500/40 bg-red-500/5 p-6 rounded-sm text-sm text-red-700" data-testid="cert-not-found">We could not find that certificate. Check the number and try again.</div>}

        {d && (
          <div className="space-y-4" data-testid="cert-view">
            {/* Environment banner — Demonstration / Sandbox / Pilot only */}
            <EnvironmentBanner record={d} />
          <div className="grid lg:grid-cols-[1fr_400px] gap-6">
            {/* Left — sanitised details */}
            <div className="border border-border bg-card p-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold ${outcomeTone(d.outcome)}`} data-testid="cert-outcome">
                  {outcomeIcon(d.outcome)} {d.outcome_label}
                </span>
                <EnvironmentChip record={d} testid="cert-env-chip" />
                {d.trust_score != null && (
                  <span className="mono text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm bg-primary/10 text-primary">
                    Trust {d.trust_score}/100
                  </span>
                )}
                <span className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground ml-auto">v{d.version}</span>
              </div>

              <div className="mt-4 mono text-[11px] uppercase tracking-widest text-primary">{d.certificate_number}</div>
              <h2 className="text-xl font-semibold mt-1">{d.asset_description || d.asset_ref || d.asset_id}</h2>
              <div className="text-[12.5px] text-muted-foreground mt-0.5">{d.country}{d.location ? ` · ${d.location}` : ""} · <span className="uppercase mono text-[10.5px]">{d.asset_type}</span></div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-6 text-[12.5px]">
                <Row l="Verification Reference" v={d.verification_reference} mono />
                <Row l="Digital Passport" v={d.digital_passport} mono />
                <Row l="Asset Category" v={d.asset_category} />
                <Row l="Verification Level" v={d.verification_level} />
                <Row l="Verified Date" v={fmt(d.verified_at)} />
                <Row l="Issue Date" v={fmt(d.issued_at)} />
                <Row l="Expiry / Review Date" v={fmt(d.expires_at)} />
                <Row l="Issuing Officer" v={d.issuing_officer} />
                <Row l="Last Status Update" v={fmt(d.last_status_update)} />
                <Row l="Issuing Entity" v={d.issuing_entity} />
              </div>

              {(d.scope_of_checks || []).length > 0 && (
                <div className="mt-5">
                  <div className="kicker">Scope of checks completed</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {d.scope_of_checks.map((s, i) => (
                      <span key={i} className="text-[11.5px] px-2 py-1 border border-border rounded-sm">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {(d.material_disclosures || []).length > 0 && (
                <div className="mt-5">
                  <div className="kicker text-amber-700">Material disclosures</div>
                  <ul className="mt-2 space-y-1.5">
                    {d.material_disclosures.map((s, i) => (
                      <li key={i} className="text-[12.5px] border-l-2 border-amber-500/60 pl-2">{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 mono text-[10.5px] text-muted-foreground border-t border-border pt-4">
                <div>SHA-256 hash: <code className="text-[10.5px]">{d.certificate_hash?.slice(0, 32)}…</code></div>
                <div className="mt-1 italic">{d.disclaimer}</div>
              </div>
            </div>

            {/* Right — Actions + live badge previews */}
            <aside>
              <div className="border border-border bg-card p-5 mb-4">
                <div className="kicker">Downloads & Sharing</div>
                <div className="mt-3 space-y-2">
                  <a href={pdfUrl()} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="cert-download-pdf"><Download className="h-3.5 w-3.5 mr-2" /> Download Verified Certificate (PDF)</Button></a>
                  <a href={badgeUrl("square", "svg")} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="cert-download-square"><Download className="h-3.5 w-3.5 mr-2" /> Download Square Badge (SVG)</Button></a>
                  <a href={badgeUrl("horizontal", "svg")} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="cert-download-horizontal"><Download className="h-3.5 w-3.5 mr-2" /> Download Web Badge (SVG)</Button></a>
                  <a href={badgeUrl("circular", "svg")} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="cert-download-circular"><Download className="h-3.5 w-3.5 mr-2" /> Download Certification Stamp (SVG)</Button></a>
                  <a href={badgeUrl("label", "svg")} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="w-full justify-start" data-testid="cert-download-label"><Printer className="h-3.5 w-3.5 mr-2" /> Print Asset Label (SVG)</Button></a>
                  <Button size="sm" variant="outline" className="w-full justify-start" onClick={share} data-testid="cert-share"><Share2 className="h-3.5 w-3.5 mr-2" /> Share Verification Link</Button>
                  <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => copy(d.certificate_number)} data-testid="cert-copy-number"><Copy className="h-3.5 w-3.5 mr-2" /> Copy Certificate Number</Button>
                </div>
              </div>

              <div className="border border-border bg-card p-5">
                <div className="kicker">Live badge preview</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="border border-border rounded-sm p-2 bg-black/5 flex justify-center">
                    <img src={badgeUrl("square")} alt="Square" className="max-w-full h-auto" data-testid="cert-preview-square" />
                  </div>
                  <div className="border border-border rounded-sm p-2 bg-black/5 flex justify-center">
                    <img src={badgeUrl("circular")} alt="Circular" className="max-w-full h-auto" data-testid="cert-preview-circular" />
                  </div>
                  <div className="col-span-2 border border-border rounded-sm p-2 bg-black/5">
                    <img src={badgeUrl("horizontal")} alt="Horizontal" className="w-full h-auto" data-testid="cert-preview-horizontal" />
                  </div>
                </div>
                <p className="text-[10.5px] text-muted-foreground mt-3">
                  Badge status is rendered live at every request. Do not rely on the appearance of a saved image — the registry is the authoritative source.
                </p>
              </div>

              <div className="mt-4 mono text-[10.5px] uppercase tracking-widest text-muted-foreground">
                <a href={d.public_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  Public URL <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </aside>
          </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ l, v, mono }) => (
  <>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{l}</div>
    <div className={mono ? "mono text-primary" : ""}>{v || "—"}</div>
  </>
);

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "long", year: "numeric" }); } catch { return iso; }
};
